import * as React from "react";
import {
  Typography,
  Popover,
  Box,
  MenuItem,
  Button,
  DialogTitle,
  Modal,
  Menu,
  Switch,
  TextField,
  Input,
  IconButton,
  Tooltip,
  Divider, Grid,
} from "@mui/material";

import reorder from "../../../../public/assets/icons/reorder.svg";
import cancel from "../../../../public/assets/icons/cancel.svg";
import search from "../../../../public/assets/icons/search.svg";
import { scrollStyles } from "../../../theme/Theme";
import { styled } from "@mui/material/styles";

type Props = {
  anchorEl: any;
  handleClose: any;
  options: Option[];
  onHide: any;
  onSelect: any;
  setOptions: any;
  onOrderChange?: any;
  selected?: any;
  handleRenameColumnName?: any;
  handleShowAll?: any;
  handleHideAll?: any;
};
type Option = {
  id: string;
  field: string;
  headerName: string;
  hide: boolean;
  isMeta?: boolean;
};

export const OptionsMenu = ({
  anchorEl,
  handleClose,
  options,
  setOptions,
  onHide,
  onSelect,
  selected,
  handleRenameColumnName,
  handleHideAll,
  handleShowAll,
  onOrderChange,
  ...props
}: Props) => {
  const [searchValue, setSearchValue] = React.useState<string>("");
  const dragItem = React.useRef();
  const dragOverItem = React.useRef();
  const [renameLabelOfField, setRenameLabelOfField] =
    React.useState<string>("");
  const [optionsDict, setOptionsDict] = React.useState<any>({});
  const [values, setValues] = React.useState<any>(options);

  const dragStart = (e, position) => {
    dragItem.current = position;
  };
  const dragEnter = (e, position) => {
    dragOverItem.current = position;
  };
  const drop = (e) => {
    const copyOptions = [...options];
    //@ts-ignore
    const dragItemContent = copyOptions[dragItem.current];
    copyOptions.splice(dragItem.current, 1);
    copyOptions.splice(dragOverItem.current, 0, dragItemContent);
    dragItem.current = null;
    dragOverItem.current = null;
    setOptions(copyOptions);
    setValues(copyOptions);
    onOrderChange && onOrderChange(copyOptions);
  };

  React.useEffect(() => {
    if (searchValue?.length) {
      const l = options?.filter((option) =>
        option?.headerName?.toLowerCase().includes(searchValue)
      );
      setValues(l);
    } else {
      setValues(options);
    }
    const dict = {};
    options?.map((option) => {
      dict[option?.field] = option?.headerName;
    });
    setOptionsDict(dict);
  }, [options]);

  React.useEffect(() => {
    setValues(options);
  }, []);

  const handleKeyPress = (e, id, newValue) => {
    if (e.key === "Enter") {
      handleRenameColumnName(id, newValue);
      setRenameLabelOfField("");
    }
  };
  const handleChangeTextField = (e, fieldName: any) => {
    const value = e.target.value;
    const dict = { ...optionsDict };
    dict[fieldName] = value;
    setOptionsDict(dict);
  };
  const renderName = (option: any) => {
    if (option?.field === renameLabelOfField) {
      return (
        <TextField
          value={optionsDict[option?.field]}
          onChange={(e) => handleChangeTextField(e, option?.field)}
          onKeyPress={(e) => {
            handleKeyPress(e, option?.colId ?? option?.id, optionsDict[option?.field]);
          }}
          size="small"
        />
      );
    } else {
      return (
        <Typography variant="body1" sx={{ color: "artisticTextHigh.main" }}>
          {option?.headerName}
        </Typography>
      );
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value?.toLowerCase();
    setSearchValue(value);
    const l = options?.filter((option) =>
      option?.headerName?.toLowerCase().includes(value)
    );
    setValues(l);
  };

  const handleClearSearch = () => {
    setSearchValue("");
    setValues(options);
  };

  const StyledButton = styled(Button)({
    borderRadius: "4px",
    textTransform: "none",
    flexGrow: 1,
  });

  const content = (option) => {
    return (
      <Box display="flex" flexDirection="column" rowGap="5px">
        <Typography variant="caption1">{option?.field}</Typography>
        <Typography variant="overline">{option?.context?.description ?? option?.description}</Typography>
      </Box>
    );
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      MenuListProps={{
        sx: {
          margin: "5px",
          width: "fit-content",
          minWidth: "300px",
        },
      }}
    >
      <Grid container flexDirection={"column"}>
        <Input
          sx={{
            padding: "0px 0px 0px 0px",
            margin: "15px 15px 0px 15px",
            width: `calc(100% - 30px)`,
            height: "fit-content",
          }}
          disableUnderline
          value={searchValue}
          placeholder="Find a field"
          onChange={handleSearch}
          onKeyDown={(e) => e.stopPropagation()}
          endAdornment={
            searchValue?.length ? (
              <IconButton onClick={handleClearSearch} disableRipple={true}>
                <img src={cancel} alt="cancel" style={{ height: "14px" }} />
              </IconButton>
            ) : (
              <IconButton disableRipple={true}>
                <img src={search} alt="search" style={{ height: "14px" }} />
              </IconButton>
            )
          }
        />
        <Divider
          orientation="horizontal"
          flexItem
          sx={{
            border: `1px solid #cfd1d0`,
            ml: "15px",
            mr: "15px",
            mb: "8px",
          }}
        />
        <Box
          sx={{
            maxHeight: "350px",
            height: "100%",
            overflowY: "scroll",
            ...scrollStyles().scrollBar,
          }}
        >
          {values
            ?.filter((option) => !option.context?.isMeta)
            .map((option, index) => (
              <MenuItem
                key={option?.field}
                sx={{
                  ":hover": {
                    backgroundColor: "transparent",
                  },
                  padding: "8px 16px",
                  gap: "8px",
                }}
                selected={option.field === selected?.field}
                disableRipple
                value={option?.field}
                onDragStart={(e) => dragStart(e, index)}
                onDragEnter={(e) => dragEnter(e, index)}
                onDragEnd={drop}
                draggable
                onKeyDown={(e) => e.stopPropagation()}
              >
                <Switch
                  defaultChecked
                  size="small"
                  checked={!option?.hide}
                  onChange={() => onHide(option)}
                  sx={{}}
                />
                <Tooltip title={content(option)} arrow placement="right">
                  <Box
                    sx={{
                      display: "flex",
                      width: "100%",
                      gap: "5px",
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      sx={{ justifyContent: "start", width: "100%" }}
                      disableRipple
                      // onClick={() => {
                      //   onSelect(option);
                      // }}
                      onDoubleClick={() => {
                        if (option?.colId ?? option?.id) {
                          setRenameLabelOfField(option?.field);
                        }
                      }}
                    >
                      {renderName(option)}
                    </Button>
                    <img src={reorder} alt="reorder" />
                  </Box>
                </Tooltip>
              </MenuItem>
            ))}
        </Box>
        {searchValue?.length ? (
          <></>
        ) : (
          <Box
            display="flex"
            flexDirection="row"
            columnGap="10px"
            margin="8px 15px 2px"
          >
            <StyledButton variant="outlined" onClick={handleHideAll}>Hide All</StyledButton>
            <StyledButton variant="outlined" onClick={handleShowAll}>Show All</StyledButton>
          </Box>
        )}
      </Grid>
    </Menu>
  );
};
